import {
  OwnerBarcodeSkuLookupItem,
  ProductOptionSkuLookupItem,
  SKU_LOOKUP_PLATFORMS,
  SkuLookupOutcome,
  SkuLookupPlatform,
} from '@/console/api/types'
import { TagProps } from '@/console/ui/Tag'

/** SKU 조회 패널의 순수 로직. 입력 파싱 · 판정 톤 · 집계는 여기 두고 패널은 결과만 그린다. */

/** SKU 조회 · 백필 세 API 의 게이트(`allowsOperation`)는 콘솔 주문 조회보다 넓어 권한 문구도 따로 둔다. */
export const SKU_FORBIDDEN_MESSAGE = '이 조치에 권한이 없어요. (MOMS_ENG · MUSINSA12 · MUSINSA0 · MASTER 중 하나 필요)'

/** 서버는 중복을 합친 뒤의 건수로 1~100건을 받는다. */
export const SKU_LOOKUP_MAX_ITEMS = 100

export const SKU_LOOKUP_AXIS = { productOption: 'productOption', ownerBarcode: 'ownerBarcode' } as const
export type SkuLookupAxis = (typeof SKU_LOOKUP_AXIS)[keyof typeof SKU_LOOKUP_AXIS]

export type Tone = TagProps['tone']

/** `LOOKUP_FAILED`(재시도 대상)와 `UNMAPPED`(매핑 등록 문의 대상)는 운영자가 다르게 움직여야 해서 톤을 가른다. */
export const SKU_LOOKUP_OUTCOME_TONE = {
  FOUND: 'good',
  UNMAPPED: 'strong',
  LOOKUP_FAILED: 'warn',
  UNKNOWN_SHIPPER: 'crit',
  UNSUPPORTED_SERVICE_TYPE: 'neutral',
} as const satisfies Record<SkuLookupOutcome, Tone>

export const SKU_LOOKUP_NEXT_ACTION = {
  FOUND: 'skuId 를 백필 입력에 쓴다',
  UNMAPPED: 'SCM 에 매핑 등록을 문의한다',
  LOOKUP_FAILED: '같은 요청을 다시 보낸다',
  UNKNOWN_SHIPPER: '화주코드를 고친다',
  UNSUPPORTED_SERVICE_TYPE: 'MCD 화주라 이 축으로는 못 찾는다',
} as const satisfies Record<SkuLookupOutcome, string>

export const SKU_LOOKUP_OUTCOMES = Object.keys(SKU_LOOKUP_OUTCOME_TONE) as SkuLookupOutcome[]

export type PastedLines<T> = { items: T[]; invalidLines: string[]; duplicates: number }

const splitTokens = (line: string): string[] => line.split(/[\s,]+/).filter((token) => token.length > 0)

/** 한 줄에 한 건. 줄 안은 공백 · 탭 · 쉼표로 가른다. 같은 키는 한 건으로 합치고 입력 순서를 지킨다. */
export const parsePastedLines = <T>(raw: string, parseTokens: (tokens: string[]) => T | null, keyOf: (item: T) => string): PastedLines<T> => {
  const seenKeys = new Set<string>()
  const result: PastedLines<T> = { items: [], invalidLines: [], duplicates: 0 }

  raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .forEach((line) => {
      const item = parseTokens(splitTokens(line))
      if (item === null) {
        result.invalidLines.push(line)
        return
      }
      const key = keyOf(item)
      if (seenKeys.has(key)) {
        result.duplicates += 1
        return
      }
      seenKeys.add(key)
      result.items.push(item)
    })

  return result
}

/** 1 이상의 안전 정수만 받는다 — 범위를 넘으면 JS 가 값을 뭉개 엉뚱한 건을 보낸다. */
export const parsePositiveId = (token: string): number | null => {
  if (!/^\d+$/.test(token)) return null
  const value = Number(token)
  return value > 0 && Number.isSafeInteger(value) ? value : null
}

const isSkuLookupPlatform = (token: string): token is SkuLookupPlatform => (SKU_LOOKUP_PLATFORMS as readonly string[]).includes(token)

const parseProductOptionTokens = (tokens: string[]): ProductOptionSkuLookupItem | null => {
  if (tokens.length !== 3) return null
  const [platformToken, uidToken, optionCodeToken] = tokens
  const platform = platformToken.toUpperCase()
  const uid = parsePositiveId(uidToken)
  const optionCode = parsePositiveId(optionCodeToken)
  if (!isSkuLookupPlatform(platform) || uid === null || optionCode === null) return null
  return { platform, uid, optionCode }
}

const parseOwnerBarcodeTokens = (tokens: string[]): OwnerBarcodeSkuLookupItem | null => {
  if (tokens.length !== 2) return null
  const [shipperCode, barcode] = tokens
  return { shipperCode, barcode }
}

/** 상품옵션 축 — 한 줄에 `platform uid optionCode`. */
export const parseProductOptionLines = (raw: string): PastedLines<ProductOptionSkuLookupItem> =>
  parsePastedLines(raw, parseProductOptionTokens, (item) => `${item.platform}:${item.uid}:${item.optionCode}`)

/** 화주+바코드 축 — 한 줄에 `shipperCode barcode`. */
export const parseOwnerBarcodeLines = (raw: string): PastedLines<OwnerBarcodeSkuLookupItem> =>
  parsePastedLines(raw, parseOwnerBarcodeTokens, (item) => `${item.shipperCode}\n${item.barcode}`)

export const exceedsLookupLimit = (parsed: PastedLines<unknown>): boolean => parsed.items.length > SKU_LOOKUP_MAX_ITEMS

/** 유효하지 않은 줄이 하나라도 있으면 막는다 — 일부만 조회되면 나머지가 조회된 줄 알고 넘어간다. */
export const isSubmittableLookup = (parsed: PastedLines<unknown>): boolean =>
  parsed.items.length > 0 && parsed.invalidLines.length === 0 && !exceedsLookupLimit(parsed)

/** `FOUND 2 · UNMAPPED 6` 처럼 0건이 아닌 판정만 정해진 순서로 한 줄에 싣는다. */
export const formatOutcomeSummary = <O extends string>(outcomes: O[], order: readonly O[]): string => {
  const counts = order.map((outcome) => ({ outcome, count: outcomes.filter((value) => value === outcome).length }))
  const nonZero = counts.filter(({ count }) => count > 0).map(({ outcome, count }) => `${outcome} ${count}`)
  return `총 ${outcomes.length}건 — ${nonZero.join(' · ')}`
}
